import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, collectionGroup, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { companyName, errorMessage, formatDate, roleLabel } from '../lib/format';

const FILTERS = [
  { id: 'all', label: 'Todos' },
  { id: 'admin', label: 'Administradores' },
  { id: 'employee', label: 'Empleados' },
  { id: 'suspended', label: 'Suspendidos' },
];

function Users() {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        // companies/{id}/users de todas las tiendas.
        const [usersSnap, companiesSnap] = await Promise.all([
          getDocs(collectionGroup(db, 'users')),
          getDocs(collection(db, 'companies')),
        ]);
        setCompanies(Object.fromEntries(companiesSnap.docs.map((d) => [d.id, { id: d.id, ...d.data() }])));
        setUsers(usersSnap.docs
          .map((d) => ({ id: d.id, ...d.data(), companyId: d.ref.parent.parent?.id || 'N/A' }))
          .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)));
      } catch (err) {
        console.error('Error fetching users:', err);
        setError(errorMessage(err));
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  const term = search.trim().toLowerCase();
  const visible = users.filter((u) => {
    // El rol vive en `role`; el panel leía un `isAdmin` que la app nunca
    // guarda, así que todos salían como empleados.
    if (filter === 'admin' && u.role !== 'admin') return false;
    if (filter === 'employee' && u.role === 'admin') return false;
    if (filter === 'suspended' && u.active !== false) return false;
    if (!term) return true;
    return [u.name, u.username, u.email, u.id, companyName(companies[u.companyId])]
      .some((v) => String(v || '').toLowerCase().includes(term));
  });

  const counts = {
    all: users.length,
    admin: users.filter((u) => u.role === 'admin').length,
    employee: users.filter((u) => u.role !== 'admin').length,
    suspended: users.filter((u) => u.active === false).length,
  };

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Usuarios</h1>
          <p className="page-subtitle">Cuentas de todas las tiendas</p>
        </div>
        <input
          type="search"
          className="input-field search-field"
          placeholder="Buscar por nombre, usuario, correo o tienda"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="tabs" role="tablist">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            role="tab"
            aria-selected={filter === f.id}
            className={`tab-btn ${filter === f.id ? 'active' : ''}`}
            onClick={() => setFilter(f.id)}
          >
            {f.label} <span className="tab-count">{counts[f.id]}</span>
          </button>
        ))}
      </div>

      {error && <div className="notice notice-error">No se pudieron cargar los usuarios. {error}</div>}

      <div className="card">
        <div className="table-container">
          {loading ? (
            <div className="empty-state"><span className="spinner spinner-dark" /></div>
          ) : visible.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">👥</div>
              <p>{users.length ? 'Ningún usuario coincide' : 'No hay usuarios registrados'}</p>
            </div>
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Acceso</th>
                  <th>Tienda</th>
                  <th>Rol</th>
                  <th>Estado</th>
                  <th>Alta</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((u) => (
                  <tr key={`${u.companyId}/${u.id}`}>
                    <td>
                      <div className="font-semibold">{u.name || 'Sin nombre'}</div>
                      <div className="mono-text">{u.id}</div>
                    </td>
                    <td className="text-secondary">
                      {u.username && <div>@{u.username}</div>}
                      <div className="text-xs">{u.email || '—'}</div>
                    </td>
                    <td>
                      <Link className="table-link" to={`/companies/${u.companyId}`}>
                        {companyName(companies[u.companyId] || { id: u.companyId })}
                      </Link>
                    </td>
                    <td><span className={`badge ${u.role === 'admin' ? 'badge-dark' : 'badge-neutral'}`}>{roleLabel(u.role)}</span></td>
                    <td><span className={`badge ${u.active === false ? 'badge-danger' : 'badge-success'}`}>{u.active === false ? 'Suspendido' : 'Activo'}</span></td>
                    <td className="text-secondary">{formatDate(u.createdAt, false)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default Users;
